/**
 * Provider Status Badge - Shows active LLM provider and authentication status
 *
 * Displays:
 * - Current provider (Claude, OpenAI Codex, OpenAI API)
 * - Current model
 * - Connection status (green dot = connected, red dot = not authenticated)
 *
 * Clicking opens Settings dialog (via callback)
 */

import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Cpu, Check, X, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";
import { cn } from "../lib/utils";
import type { LLMProvider } from "../../shared/types/settings";

interface ProviderStatusBadgeProps {
  className?: string;
  /** Callback to open settings dialog */
  onSettingsClick?: () => void;
}

interface ProviderState {
  provider: LLMProvider;
  model: string;
  authenticated: boolean;
  providerName: string;
}

type StatusType = "loading" | "connected" | "not-connected" | "error";

/**
 * Provider Status Badge for the sidebar.
 * Shows which LLM provider is active and its connection status.
 */
export function ProviderStatusBadge({ className, onSettingsClick }: ProviderStatusBadgeProps) {
  const { t } = useTranslation(["common"]);
  const [status, setStatus] = useState<StatusType>("loading");
  const [providerState, setProviderState] = useState<ProviderState | null>(null);

  // Fetch current provider and auth status
  const checkProvider = useCallback(async () => {
    try {
      if (!window.electronAPI?.provider) {
        setStatus("error");
        return;
      }

      // Get current provider
      const currentResult = await window.electronAPI.provider.getCurrent();
      if (!currentResult.success || !currentResult.data) {
        setStatus("error");
        return;
      }

      const { provider, model } = currentResult.data;

      // Get available providers to check auth status
      const availableResult = await window.electronAPI.provider.getAvailable();
      if (!availableResult.success || !availableResult.data) {
        setStatus("error");
        return;
      }

      // Find the current provider in the available list
      const providerInfo = availableResult.data.find((p) => p.id === provider);
      if (!providerInfo) {
        setStatus("error");
        return;
      }

      // Map provider id to display name
      const providerNames: Record<LLMProvider, string> = {
        claude: t("common:provider.names.claude", "Claude"),
        codex: t("common:provider.names.codex", "OpenAI Codex"),
        openai_api: t("common:provider.names.openaiApi", "OpenAI API"),
      };

      setProviderState({
        provider,
        model,
        authenticated: providerInfo.authenticated,
        providerName: providerNames[provider] || provider,
      });

      setStatus(providerInfo.authenticated ? "connected" : "not-connected");
    } catch (err) {
      console.error("[ProviderStatusBadge] Error checking provider:", err);
      setStatus("error");
    }
  }, [t]);

  // Check on mount and set up auth change listener
  useEffect(() => {
    checkProvider();

    // Listen for auth changes
    const cleanup = window.electronAPI?.provider?.onAuthChanged?.(() => {
      checkProvider();
    });

    return () => {
      cleanup?.();
    };
  }, [checkProvider]);

  // Open settings when clicked
  const handleClick = () => {
    onSettingsClick?.();
  };

  // Status indicator colors
  const statusColors: Record<StatusType, string> = {
    loading: "bg-gray-400",
    connected: "bg-green-500",
    "not-connected": "bg-red-500",
    error: "bg-yellow-500",
  };

  // Status icons
  const StatusIcon = () => {
    switch (status) {
      case "loading":
        return <Loader2 className="h-3 w-3 animate-spin" />;
      case "connected":
        return <Check className="h-3 w-3" />;
      case "not-connected":
        return <X className="h-3 w-3" />;
      default:
        return null;
    }
  };

  // Tooltip content
  const getTooltipContent = () => {
    if (status === "loading") {
      return t("common:labels.loading", "Loading...");
    }
    if (status === "error" || !providerState) {
      return t("common:provider.badge.notConfigured", "Not configured");
    }

    const statusText =
      status === "connected"
        ? t("common:provider.status.connected", "Connected")
        : t("common:provider.status.notConnected", "Not connected");

    return (
      <div className="space-y-1">
        <div className="font-medium">
          {t("common:provider.badge.usingProvider", {
            provider: providerState.providerName,
            defaultValue: `Using ${providerState.providerName}`,
          })}
        </div>
        <div className="text-xs text-muted-foreground">
          {t("common:provider.badge.model", {
            model: providerState.model,
            defaultValue: `Model: ${providerState.model}`,
          })}
        </div>
        <div className="text-xs flex items-center gap-1">
          <span
            className={cn(
              "w-2 h-2 rounded-full",
              statusColors[status]
            )}
          />
          {statusText}
        </div>
        <div className="text-xs text-muted-foreground mt-1">
          {t("common:provider.status.clickToConfigure", "Click to configure")}
        </div>
      </div>
    );
  };

  // Short display text for the badge
  const getDisplayText = () => {
    if (status === "loading") {
      return "...";
    }
    if (!providerState) {
      return t("common:provider.badge.notConfigured", "Not configured");
    }
    return providerState.providerName;
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClick}
          className={cn(
            "h-7 px-2 gap-1.5 text-xs font-normal text-muted-foreground hover:text-foreground",
            className
          )}
        >
          <Cpu className="h-3.5 w-3.5" />
          <span className="truncate max-w-[100px]">{getDisplayText()}</span>
          <span
            className={cn(
              "w-2 h-2 rounded-full shrink-0",
              statusColors[status]
            )}
          />
          <StatusIcon />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="top" align="start" className="max-w-[250px]">
        {getTooltipContent()}
      </TooltipContent>
    </Tooltip>
  );
}
